/**
 * Payment API Client
 *
 * Typed fetch functions wrapping /api/payment/ endpoints.
 * All functions use the shared API client from @shared/lib/api/client.
 *
 * @module payment/api/payment-client
 */

import { apiClient } from "@shared/lib/api/client";
import type {
  Subscription,
  PricingTier,
  CheckoutSession,
  CheckoutFormData,
  CouponValidation,
  Invoice,
  UsageSummary,
  Entitlement,
  PaymentMethod,
  PaymentMethodUpdate,
  CreditPack,
  MetaPaginatedResponse,
  WalletBalance,
  WalletTransaction,
  LedgerEntry,
  CursorPaginatedResponse,
  CreditPackCheckoutSession,
  CreateCreditPackCheckoutRequest,
  CostEstimateRequest,
  CostEstimateResponse,
  RefundRequest,
  RefundResponse,
} from "./types";

const PAYMENT_BASE = "/api/payment";

function cursorFromNext(next?: string | null): string | null {
  if (!next) return null;
  try {
    const base =
      typeof window === "undefined" ? "http://localhost" : window.location.origin;
    return new URL(next, base).searchParams.get("cursor");
  } catch {
    return null;
  }
}

function normalizeCursorPage<T>(
  page: CursorPaginatedResponse<T> & {
    next?: string | null;
    previous?: string | null;
  }
): CursorPaginatedResponse<T> {
  return {
    results: page.results ?? [],
    nextCursor: page.nextCursor ?? cursorFromNext(page.next),
    count: page.count,
  };
}

/**
 * Get current user subscription
 */
export async function getSubscription(): Promise<Subscription> {
  return apiClient.get<Subscription>(`${PAYMENT_BASE}/subscription/`);
}

/**
 * Cancel the current subscription
 */
export async function cancelSubscription(): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>(
    `${PAYMENT_BASE}/subscription/cancel/`
  );
}

/**
 * Change subscription plan
 */
export async function changePlan(
  planId: string
): Promise<{ success: boolean; subscription: Subscription }> {
  return apiClient.post<{ success: boolean; subscription: Subscription }>(
    `${PAYMENT_BASE}/subscription/change/`,
    { planId }
  );
}

/**
 * Get available pricing tiers
 */
export async function getPricingTiers(): Promise<PricingTier[]> {
  return apiClient.get<PricingTier[]>(`${PAYMENT_BASE}/pricing/`);
}

/**
 * Create a checkout session
 */
export async function createCheckoutSession(
  data: CheckoutFormData
): Promise<CheckoutSession> {
  return apiClient.post<CheckoutSession>(`${PAYMENT_BASE}/checkout/`, data);
}

/**
 * Validate and apply a coupon code
 */
export async function applyCoupon(code: string): Promise<CouponValidation> {
  return apiClient.post<CouponValidation>(
    `${PAYMENT_BASE}/coupon/validate/`,
    { code }
  );
}

/**
 * Get billing history (paginated invoices)
 */
export async function getBillingHistory(params: {
  page?: number;
  pageSize?: number;
}): Promise<{ results: Invoice[]; count: number }> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set("page", String(params.page));
  if (params.pageSize) searchParams.set("page_size", String(params.pageSize));
  return apiClient.get<{ results: Invoice[]; count: number }>(
    `${PAYMENT_BASE}/invoices/?${searchParams.toString()}`
  );
}

/**
 * Get current usage metrics
 */
export async function getUsage(): Promise<UsageSummary> {
  return apiClient.get<UsageSummary>(`${PAYMENT_BASE}/usage/`);
}

/**
 * Get current user entitlements
 */
export async function getEntitlements(): Promise<Entitlement[]> {
  return apiClient.get<Entitlement[]>(`${PAYMENT_BASE}/entitlements/`);
}

/**
 * Update payment method
 */
export async function updatePaymentMethod(
  data: PaymentMethodUpdate
): Promise<PaymentMethod> {
  return apiClient.put<PaymentMethod>(
    `${PAYMENT_BASE}/payment-method/`,
    data
  );
}

/**
 * Get all saved payment methods
 */
export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  return apiClient.get<PaymentMethod[]>(`${PAYMENT_BASE}/payment-methods/`);
}

/**
 * Delete a payment method
 */
export async function deletePaymentMethod(
  id: string
): Promise<{ success: boolean }> {
  return apiClient.delete<{ success: boolean }>(
    `${PAYMENT_BASE}/payment-methods/${id}/`
  );
}

// ---------------------------------------------------------------------------
// Credit Packs
// ---------------------------------------------------------------------------

/**
 * List available credit packs
 */
export async function getCreditPacks(): Promise<CreditPack[]> {
  const response = await apiClient.get<MetaPaginatedResponse<CreditPack> | CreditPack[]>(
    `${PAYMENT_BASE}/credit-packs/`
  );
  return Array.isArray(response) ? response : response.results;
}

// ---------------------------------------------------------------------------
// Wallet
// ---------------------------------------------------------------------------

/**
 * Get current user wallet balance
 */
export async function getWallet(): Promise<WalletBalance> {
  return apiClient.get<WalletBalance>(`${PAYMENT_BASE}/wallet/me/`);
}

/**
 * Get wallet transaction history (cursor-paginated)
 */
export async function getWalletTransactions(
  cursor?: string
): Promise<CursorPaginatedResponse<WalletTransaction>> {
  const url = cursor
    ? `${PAYMENT_BASE}/wallet/me/transactions/?cursor=${encodeURIComponent(cursor)}`
    : `${PAYMENT_BASE}/wallet/me/transactions/`;
  const response = await apiClient.get<
    CursorPaginatedResponse<WalletTransaction> & { next?: string | null }
  >(url);
  return normalizeCursorPage(response);
}

/**
 * Get wallet ledger entries (cursor-paginated)
 */
export async function getLedger(
  cursor?: string
): Promise<CursorPaginatedResponse<LedgerEntry>> {
  const url = cursor
    ? `${PAYMENT_BASE}/ledger/?cursor=${encodeURIComponent(cursor)}`
    : `${PAYMENT_BASE}/ledger/`;
  const response = await apiClient.get<
    CursorPaginatedResponse<LedgerEntry> & { next?: string | null }
  >(url);
  return normalizeCursorPage(response);
}

// ---------------------------------------------------------------------------
// Credit-pack Checkout
// ---------------------------------------------------------------------------

/**
 * Create a checkout session for a credit pack
 */
export async function createCreditPackCheckout(
  data: CreateCreditPackCheckoutRequest
): Promise<CreditPackCheckoutSession> {
  const { slug, ...payload } = data;
  return apiClient.post<CreditPackCheckoutSession>(
    `${PAYMENT_BASE}/checkout/credit-pack/${encodeURIComponent(slug)}/`,
    payload
  );
}

// ---------------------------------------------------------------------------
// AI Cost Estimate
// ---------------------------------------------------------------------------

/**
 * Estimate the cost in credits for an AI feature call
 */
export async function estimateCost(
  data: CostEstimateRequest
): Promise<CostEstimateResponse> {
  return apiClient.post<CostEstimateResponse>(
    `${PAYMENT_BASE}/estimate/`,
    data
  );
}

// ---------------------------------------------------------------------------
// Refund
// ---------------------------------------------------------------------------

/**
 * Request a refund for a wallet transaction
 */
export async function requestRefund(
  data: RefundRequest
): Promise<RefundResponse> {
  return apiClient.post<RefundResponse>(
    `${PAYMENT_BASE}/refund-request/`,
    data
  );
}
